// src/components/chat/ChatWidget.jsx
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { MessageSquare, X, Send, User, Bot } from 'lucide-react';
import api from '../../api/api';
import styles from './ChatWidget.module.css';

const suggestions = [
  "Comment configurer BoondManager ?",
  "Comment fonctionne le générateur de dossier de compétences ?",
  "Parler à un humain"
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Bonjour ! Je suis l\'assistant **CVtoSkill**. Une question sur l\'utilisation de la plateforme ?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatWrapperRef = useRef(null);

  // Auto-scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { 
    if (isOpen) scrollToBottom(); 
  }, [messages, isOpen, isLoading]);

  // Fermer le chat au clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatWrapperRef.current && !chatWrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSendMessage = async (manualText = null) => {
    const textToSend = typeof manualText === 'string' ? manualText : input;
    if (!textToSend.trim() || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: textToSend }]);
    setIsLoading(true);

    try {
      const response = await api.post('/support/chat', { question: textToSend });
      if (response.data && response.data.answer) {
        setMessages(prev => [...prev, { role: 'assistant', text: response.data.answer }]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { role: 'assistant', text: "Désolé, je rencontre un problème de connexion." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.widgetWrapper} ref={chatWrapperRef}>
      
      {/* BOUTON TRIGGER (S'intègre dans la Navbar) */}
      <button 
        className={`${styles.triggerBtn} ${isOpen ? styles.triggerBtnActive : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Ouvrir l'assistant IA"
      >
        <MessageSquare size={18} strokeWidth={2} />
      </button>

      {/* PANNEAU DE CHAT DROPDOWN */}
      <div className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}>
        
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
             <div className={styles.headerIcon}>
                <Bot size={18} strokeWidth={2.5}/> 
             </div>
             <div>
                <h4 className={styles.headerTitle}>Assistant IA</h4>
                <span className={styles.headerSubtitle}>Propulsé par CVtoSkill</span>
             </div>
          </div>
          <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* Messages */}
        <div className={styles.messagesArea}>
          {messages.map((msg, index) => {
            const isUser = msg.role === 'user';
            return (
              <div key={index} className={`${styles.messageRow} ${isUser ? styles.messageRowUser : ''}`}>
                
                {!isUser && (
                  <div className={`${styles.avatar} ${styles.avatarBot}`}>
                    <Bot size={14} strokeWidth={2.5} />
                  </div>
                )}
                
                <div className={`${styles.bubble} ${isUser ? styles.bubbleUser : styles.bubbleAssistant}`}>
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>

                 {isUser && (
                  <div className={`${styles.avatar} ${styles.avatarUser}`}>
                    <User size={14} strokeWidth={2} />
                  </div>
                )}
              </div>
            );
          })}

          {isLoading && (
            <div className={styles.messageRow}>
              <div className={`${styles.avatar} ${styles.avatarBot}`}>
                <Bot size={14} strokeWidth={2.5} />
              </div>
              <div className={`${styles.bubble} ${styles.bubbleAssistant} ${styles.typing}`}>
                <div className={styles.dot}></div>
                <div className={styles.dot}></div>
                <div className={styles.dot}></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {messages.length < 3 && (
            <div className={styles.suggestionsContainer}>
                {suggestions.map((sugg, index) => (
                    <button 
                        key={index} 
                        className={styles.chip}
                        onClick={() => handleSendMessage(sugg)}
                        disabled={isLoading}
                    >
                        {sugg}
                    </button>
                ))}
            </div>
        )}

        {/* Input */}
        <div className={styles.inputArea}>
          <input
            type="text"
            placeholder="Posez votre question..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
            disabled={isLoading}
          />
          <button 
            className={styles.sendBtn}
            onClick={() => handleSendMessage()} 
            disabled={isLoading || !input.trim()}
          >
            <Send size={16} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;